/**
 * Portfolio scope and search — the data behind the sidebar's Portfolios header.
 *
 * Ported from the legacy Vue app's SideBarDashboard/Portfolios.vue.
 *
 * The scope is a STRING, not a boolean: legacy's switch is
 * `true-value="Org Portfolios" false-value="My Portfolios"`, and the same
 * strings are both the model value and the visible label. They are kept
 * verbatim because the value decides which field of the `/get-portfolio`
 * response is read.
 */
import { isBillingMode } from './serviceProviders';

export const PORTFOLIO_SCOPES = Object.freeze({
  OWN: 'My Portfolios',
  TEAM: 'Org Portfolios',
});

export const DEFAULT_PORTFOLIO_SCOPE = PORTFOLIO_SCOPES.OWN;

/**
 * Which field of the `/get-portfolio` response each scope reads.
 * Legacy: `switchValue == 'My Portfolios' ? own_portfolios : team_portfolios`.
 */
export const PORTFOLIO_SCOPE_FIELD = Object.freeze({
  [PORTFOLIO_SCOPES.OWN]: 'own_portfolios',
  [PORTFOLIO_SCOPES.TEAM]: 'team_portfolios',
});

/**
 * The list for a scope, out of the normalised { own, team } response.
 * Both scopes arrive in one /get-portfolio response, so switching scope is a
 * field pick, never a refetch.
 */
export function portfoliosForScope(data, scope) {
  if (!data) return [];
  return (scope === PORTFOLIO_SCOPES.TEAM ? data.team : data.own) ?? [];
}

/** Verbatim copy from legacy. */
export const PORTFOLIO_SEARCH_PLACEHOLDER = 'Search Portfolios...';
export const NO_PORTFOLIOS_MESSAGE = 'No portfolios found';
export const CLEAR_PORTFOLIO_LABEL = 'Clear Portfolio';
export const SCOPE_ADMIN_ONLY_HINT = 'Only admins can view all portfolios';

/** The scope you land on after toggling away from `scope`. */
export function toggleScope(scope) {
  return scope === PORTFOLIO_SCOPES.TEAM ? PORTFOLIO_SCOPES.OWN : PORTFOLIO_SCOPES.TEAM;
}

/**
 * A portfolio's display name.
 *
 * Legacy portfolio objects carry the name as a DYNAMIC KEY — the first key of
 * the object — alongside fixed fields like `_id` and `provider`, which is why
 * `Object.keys(item)[0]` appears throughout Portfolios.vue and Headerbar.vue.
 * The capture lists that as deliberate, so it is reproduced rather than
 * "fixed", but isolated here instead of repeated at every call site.
 */
export function portfolioName(portfolio) {
  if (!portfolio) return '';
  if (typeof portfolio === 'string') return portfolio;
  return Object.keys(portfolio)[0] ?? '';
}

/**
 * Case-insensitive substring filter on the display name — the same matching
 * legacy's `filteredPortfolioList` computed does.
 *
 * Legacy also contains a 500ms debounce in `handleSearch`, but it writes to
 * fields that were never declared and its handler is only reachable via Enter
 * or an append icon that does not exist. It is dead code; filtering there is
 * live, and it is live here.
 */
export function filterPortfolios(portfolios = [], query = '') {
  const q = query.trim().toLowerCase();
  if (!q) return [...portfolios];
  return portfolios.filter((p) => portfolioName(p).toLowerCase().includes(q));
}

/**
 * Was this portfolio built with the General IT Average (Scaler) model?
 *
 * The API reports that model as 'scaler'; anything else — including a missing
 * value — is SPEC CPU. Legacy: `item?.model?.toLowerCase() == 'scaler'`.
 */
export function isScalerPortfolio(portfolio) {
  return String(portfolio?.model ?? '').toLowerCase() === 'scaler';
}

/** Cloud providers whose portfolios can be cloud-credential (account sync) ones. */
const CREDENTIAL_CAPABLE_PROVIDERS = Object.freeze(['AWS', 'AZURE', 'GCP', 'OCI']);

/**
 * Which mark a row shows next to its name: a provider key for PROVIDER_ICONS,
 * or 'cloud-credential' for a portfolio synced from cloud account credentials.
 *
 * Legacy's chain (Portfolios.vue:57-68) shows the cloud logo only when the
 * portfolio is NOT cloud-credential, and a key otherwise; telemetry portfolios
 * always show their connector's logo. Legacy also shows NOTHING for OCI,
 * Billing and Prometheus portfolios — an omission, not a rule — so those get
 * their logo here like every other provider.
 *
 * @returns {string | null}
 */
export function portfolioMark(portfolio) {
  const provider = portfolio?.provider;
  if (!provider) return null;
  if (portfolio.is_cloud_cred && CREDENTIAL_CAPABLE_PROVIDERS.includes(provider)) {
    return 'cloud-credential';
  }
  return provider;
}

/** Large-data statuses that are still working — legacy shows a % for these. */
const LARGE_DATA_PENDING = Object.freeze(['queue', 'to_process', 'failed', 'in_progress']);

/**
 * The progress figure for a large-data portfolio still being processed, or null
 * when there is nothing to show.
 *
 * Legacy (Portfolios.vue:91-106) shows `recommendation_percentage` (default 0)
 * while the status is queue / to_process / failed / in_progress and the figure
 * is below 100. Status casing varies on the wire (`TO_PROCESS`, `in_progress`),
 * so it is compared case-insensitively, as legacy does.
 *
 * @returns {number | null}
 */
export function largeDataProgress(portfolio) {
  if (portfolio?.is_large_data !== true) return null;
  const status = String(portfolio.recommendation_status ?? '').toLowerCase();
  const percent = Number(portfolio.recommendation_percentage) || 0;
  if (!LARGE_DATA_PENDING.includes(status) || percent >= 100) return null;
  return percent;
}

/**
 * Which refresh icon a big portfolio's row shows, or null for none.
 *
 *  - 'not-started' — status TO_PROCESS: the warning-style icon
 *    (legacy `mdi-reload-alert`);
 *  - 'refresh'     — any other unfinished status (QUEUE, IN_PROGRESS, FAILED);
 *  - null          — normal portfolio, status COMPLETED, or already at 100 %.
 *
 * Legacy (Portfolios.vue:84-90): shown while `recommendation_percentage !== 100`
 * and the status is not 'completed'. Compared case-insensitively — the server
 * sends both `IN_PROGRESS` and `in_progress`.
 *
 * @returns {'refresh' | 'not-started' | null}
 */
export function largeDataRefresh(portfolio) {
  if (portfolio?.is_large_data !== true) return null;
  const status = String(portfolio.recommendation_status ?? '').toLowerCase();
  const percent = Number(portfolio.recommendation_percentage) || 0;
  if (status === 'completed' || percent >= 100) return null;
  return status === 'to_process' ? 'not-started' : 'refresh';
}

/* --- opening a portfolio (a click on a row) --- */

/**
 * Why a click on this row cannot open it yet, or null when it can.
 *
 * A large-data portfolio opens only once its recommendation run has ended
 * (COMPLETED or FAILED) — legacy changePortfolio, Portfolios.vue:324-350, text
 * verbatim. Two legacy bugs not copied:
 *  - its `case 'inprogress'` never matches the wire value `in_progress`, so an
 *    in-progress portfolio got the vague "status is in_progress" line;
 *  - it first CLEARS the page (`this.addPortfolio()`), so clicking a busy
 *    portfolio threw away what you were working on. Here nothing changes.
 *
 * @returns {string | null}
 */
export function portfolioOpenWarning(portfolio) {
  if (portfolio?.is_large_data !== true) return null;
  const status = String(portfolio.recommendation_status ?? '').toLowerCase();
  if (status === 'completed' || status === 'failed') return null;
  const name = portfolioName(portfolio);
  switch (status) {
    case 'in_progress':
    case 'inprogress':
    case 'to_process':
    case 'queue':
      return `Recommendation for portfolio ${name} is currently ${status.replace('_', ' ')}. Please wait until it is completed.`;
    default:
      return `Recommendation status for portfolio ${name} is ${status || 'unknown'}.`;
  }
}

/**
 * Why a click on this row cannot open it yet while its TELEMETRY job runs, or
 * null when it can (user order 2026-10-27). Legacy only marked such a row with a
 * clock / spinner and still opened it, onto an empty "No instances found" list;
 * here it is not opened until the job is done — the clock / spinner is the
 * slice's telemetryJobStatus, and a finished job has no entry at all.
 *
 * @param {object} portfolio  a sidebar list item
 * @param {string} [status]   its entry in the slice's telemetryJobStatus
 * @returns {string | null}
 */
export function telemetryJobWarning(portfolio, status) {
  const mark = String(status ?? '').toLowerCase();
  if (mark !== 'pending' && mark !== 'in_progress') return null;
  const state = mark === 'pending' ? 'pending' : 'in progress';
  return `Telemetry job for portfolio ${portfolioName(portfolio)} is currently ${state}. Please wait until it is completed.`;
}

/**
 * Which screen a portfolio opens on (legacy changePortfolio's branches):
 *  - 'manage'           — Manage Portfolio, instances loaded into the table;
 *  - 'cost-advice'      — a large-data portfolio goes straight to its advice;
 *  - 'telemetry'        — any portfolio while a telemetry connector is picked;
 *  - 'cloud-credential' — a portfolio synced from cloud account credentials
 *                         (legacy's List of Instances page).
 * The page on screen carries the request out (after its own "discard
 * changes?" check); for 'cost-advice' that is a jump to the advice page.
 *
 * Billing (CCA's CUR reports) is NOT a telemetry connector here: a portfolio
 * clicked while Billing is picked opens on Manage Portfolio in Billing mode
 * (or its advice, when large). Legacy sent it to the Telemetry page
 * (TELEMETRY_NAME_MAP has 'Billing', so changePortfolio took the telemetry
 * branch) — a page with no Billing form, so its 'curreports' screen was never
 * reached by opening a portfolio.
 *
 * @returns {'manage' | 'cost-advice' | 'telemetry' | 'cloud-credential'}
 */
export function portfolioOpenTarget(portfolio, { telemetry = '' } = {}) {
  if (portfolio?.is_cloud_cred) return 'cloud-credential';
  if (telemetry && !isBillingMode(telemetry)) return 'telemetry';
  if (portfolio?.is_large_data) return 'cost-advice';
  return 'manage';
}

/**
 * What the page needs to open a portfolio — legacy's `selectedPortfolio`, cut
 * down to the fields /get-portfolio and the sidebar read.
 *
 * `billing`: opened while Billing (CUR) mode is picked. Legacy asks
 * /get-portfolio with `is_billing_data: true` then (Portfolios.vue :465), and
 * the page stays in Billing mode for it. Carried on the portfolio itself so a
 * later reload (Cancel, after Save) asks the same way. Present only when true,
 * so an ordinary open portfolio keeps its old shape.
 *
 * @param {object} portfolio  a sidebar list item
 * @param {{ billing?: boolean }} [options]
 */
export function toOpenPortfolio(portfolio, { billing = false } = {}) {
  return {
    id: portfolio?._id ?? null,
    name: portfolioName(portfolio),
    // The OWNER, not the signed-in user: an Org portfolio is someone else's,
    // and /get-portfolio looks it up under its owner (legacy `item.user`).
    user: portfolio?.user ?? '',
    provider: portfolio?.provider ?? '',
    model: portfolio?.model ?? '',
    ...(billing ? { billing: true } : {}),
  };
}
